import { Button } from "@/components/ui/button";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { WorkspaceSwitcher } from "@/components/workspace/WorkspaceSwitcher";
import {
	type SidebarNavItem,
	useSidebarKeyboardNav,
} from "@/hooks/use-sidebar-keyboard-nav";
import { useSidebarStore } from "@/lib/sidebar";
import { cn } from "@/lib/utils";
import { useWorkspaceStore } from "@/lib/workspace";
import {
	type ModuleDef,
	type ModuleNavItem,
	findActiveModule,
	getModuleDefs,
} from "@/lib/workspaces";
import { Link, useLocation, useNavigate } from "@tanstack/react-router";
import {
	ChevronsLeft,
	ChevronsRight,
	Home,
	Settings,
	Shield,
	Star,
	X,
} from "lucide-react";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

interface AppSidebarProps {
	isSuperAdmin?: boolean;
}

export function AppSidebar({ isSuperAdmin }: AppSidebarProps) {
	const { t } = useTranslation([
		"nav",
		"commerce",
		"accounting",
		"diagnostics",
	]);
	const location = useLocation();
	const {
		isOpen,
		isCollapsed,
		toggle,
		setOpen,
		toggleCollapse,
		favorites,
		addFavorite,
		removeFavorite,
	} = useSidebarStore();
	const selectedWorkspace = useWorkspaceStore((s) => s.selected());
	const wsFeatures = selectedWorkspace?.features ?? [];

	const moduleDefs = useMemo(() => getModuleDefs(), []);
	const activeModule = useMemo(
		() => findActiveModule(location.pathname),
		[location.pathname],
	);
	const navigate = useNavigate();

	const collapsed = isCollapsed;
	const width = collapsed ? "w-12" : "w-52";

	// Build flat nav item list for keyboard navigation
	const navItems = useMemo<SidebarNavItem[]>(() => {
		const items: SidebarNavItem[] = [{ path: "/", label: t("home") }];
		// Favorites
		for (const path of favorites) {
			const mod = moduleDefs.find((m) => m.items.some((i) => i.to === path));
			const item = mod?.items.find((i) => i.to === path);
			if (item)
				items.push({ path: item.to, label: t(item.labelKey), canPin: true });
		}
		// Modules + sub-items
		for (const ws of moduleDefs.filter(
			(m) => !m.feature || wsFeatures.includes(m.feature),
		)) {
			const firstTo = ws.items[0]?.to ?? ws.prefix;
			items.push({
				path: firstTo,
				label: t(ws.labelKey),
				isModule: true,
				isExpanded: activeModule?.id === ws.id,
			});
			if (activeModule?.id === ws.id && !ws.hasMenuBar) {
				for (const item of ws.items.filter(
					(i) => !i.feature || wsFeatures.includes(i.feature),
				)) {
					items.push({ path: item.to, label: t(item.labelKey), canPin: true });
				}
			}
		}
		items.push({ path: "/settings", label: t("settings") });
		return items;
	}, [moduleDefs, favorites, activeModule, wsFeatures, t]);

	const navigateTo = useCallback(
		(path: string) => {
			navigate({ to: path });
		},
		[navigate],
	);

	const { focusedIndex, handleKeyDown, resetFocus } = useSidebarKeyboardNav({
		items: navItems,
		onNavigate: navigateTo,
		onTogglePin: (path) => {
			favorites.includes(path) ? removeFavorite(path) : addFavorite(path);
		},
		onExpandModule: (path) => {
			navigateTo(path);
		},
		onCollapseModule: () => {},
	});

	const focusedPath = focusedIndex >= 0 ? navItems[focusedIndex]?.path : null;

	return (
		<>
			{/* Mobile overlay */}
			{isOpen && (
				<div
					className="fixed inset-0 z-40 bg-black/40 lg:hidden"
					onClick={() => setOpen(false)}
				/>
			)}

			<aside
				className={cn(
					"fixed inset-y-0 left-0 z-50 flex flex-col border-r bg-muted/40 transition-all duration-200 lg:static",
					width,
					isOpen
						? "translate-x-0"
						: "-translate-x-full lg:-translate-x-full lg:hidden",
				)}
			>
				{/* Header */}
				<div
					className={cn(
						"flex shrink-0 items-center border-b",
						collapsed ? "h-12 justify-center px-1" : "h-12 px-2",
					)}
				>
					{collapsed ? (
						<Tooltip>
							<TooltipTrigger asChild>
								<Button
									type="button"
									variant="ghost"
									size="icon"
									onClick={toggleCollapse}
									aria-label={t("expand_sidebar")}
								>
									<ChevronsRight className="h-4 w-4" />
								</Button>
							</TooltipTrigger>
							<TooltipContent side="right">
								{t("expand_sidebar")}
							</TooltipContent>
						</Tooltip>
					) : (
						<>
							<WorkspaceSwitcher />
							<Button
								type="button"
								variant="ghost"
								size="icon"
								className="ml-auto lg:hidden"
								onClick={toggle}
								aria-label={t("common:close")}
							>
								<X className="h-4 w-4" />
							</Button>
						</>
					)}
				</div>

				{/* Navigation */}
				<nav
					className="flex-1 overflow-y-auto px-1.5 py-2 space-y-1 outline-none"
					tabIndex={0}
					onKeyDown={handleKeyDown}
					onBlur={resetFocus}
				>
					{/* Home */}
					<NavItem
						to="/"
						icon={Home}
						label={t("home")}
						collapsed={collapsed}
						isActive={location.pathname === "/"}
						isFocused={focusedPath === "/"}
					/>

					{/* Favorites */}
					{favorites.length > 0 && !collapsed && (
						<div className="pt-2 pb-1">
							<p className="px-2 text-[10px] font-medium text-muted-foreground/50 uppercase tracking-wider">
								{t("favorites")}
							</p>
						</div>
					)}
					{favorites.map((path) => {
						const mod = moduleDefs.find((m) =>
							m.items.some((i) => i.to === path),
						);
						const item = mod?.items.find((i) => i.to === path);
						if (!item) return null;
						return (
							<NavItem
								key={path}
								to={item.to}
								icon={item.icon}
								label={t(item.labelKey)}
								collapsed={collapsed}
								isActive={location.pathname === item.to}
								isFocused={focusedPath === item.to}
								onUnpin={() => removeFavorite(path)}
							/>
						);
					})}

					{/* Modules */}
					<div className="pt-2 space-y-0.5">
						{moduleDefs
							.filter((ws) => !ws.feature || wsFeatures.includes(ws.feature))
							.map((ws) => {
								const isActive = activeModule?.id === ws.id;
								return (
									<ModuleItem
										key={ws.id}
										module={ws}
										isActive={isActive}
										collapsed={collapsed}
										wsFeatures={wsFeatures}
										t={t}
										favorites={favorites}
										onPin={addFavorite}
										onUnpin={removeFavorite}
									/>
								);
							})}
					</div>
				</nav>

				{/* Footer */}
				<div className="shrink-0 border-t px-1.5 py-2 space-y-0.5">
					{isSuperAdmin && (
						<NavItem
							to="/admin"
							icon={Shield}
							label={t("admin")}
							collapsed={collapsed}
							isActive={location.pathname.startsWith("/admin")}
						/>
					)}
					<NavItem
						to="/settings"
						icon={Settings}
						label={t("settings")}
						collapsed={collapsed}
						isActive={location.pathname.startsWith("/settings")}
						isFocused={focusedPath === "/settings"}
					/>

					{/* Collapse toggle (desktop only) */}
					{!collapsed && (
						<Button
							type="button"
							variant="ghost"
							size="sm"
							className="hidden lg:flex w-full items-center gap-2 px-2 py-1 text-xs text-muted-foreground/50 hover:text-muted-foreground justify-start"
							onClick={toggleCollapse}
							aria-label={t("collapse_sidebar")}
						>
							<ChevronsLeft className="h-3.5 w-3.5" />
							{t("collapse_sidebar")}
						</Button>
					)}
				</div>
			</aside>
		</>
	);
}

// ---------------------------------------------------------------------------
// NavItem — single navigation link with optional tooltip in collapsed mode
// ---------------------------------------------------------------------------

function NavItem({
	to,
	icon: Icon,
	label,
	collapsed,
	isActive,
	isFocused,
	onUnpin,
}: {
	to: string;
	icon: React.ComponentType<{ className?: string }>;
	label: string;
	collapsed: boolean;
	isActive: boolean;
	isFocused?: boolean;
	onUnpin?: () => void;
}) {
	const { t } = useTranslation("nav");
	const inner = (
		<Link
			to={to}
			className={cn(
				"flex items-center gap-2 rounded-md px-2 py-1.5 text-sm font-medium transition-colors group",
				collapsed && "justify-center px-0",
				isActive
					? "bg-primary-action text-primary-action-foreground"
					: "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
				isFocused && "ring-1 ring-primary ring-offset-1",
			)}
		>
			<Icon className={cn("shrink-0", collapsed ? "h-5 w-5" : "h-4 w-4")} />
			{!collapsed && <span className="flex-1 truncate">{label}</span>}
			{!collapsed && onUnpin && (
				<Button
					type="button"
					variant="ghost"
					size="icon"
					className="opacity-0 group-hover:opacity-100 h-5 w-5"
					onClick={(e) => {
						e.preventDefault();
						e.stopPropagation();
						onUnpin();
					}}
					aria-label={t("remove_from_favorites")}
					title={t("remove_from_favorites")}
				>
					<X className="h-3 w-3" />
				</Button>
			)}
		</Link>
	);

	if (collapsed) {
		return (
			<Tooltip>
				<TooltipTrigger asChild>{inner}</TooltipTrigger>
				<TooltipContent side="right" sideOffset={8}>
					{label}
				</TooltipContent>
			</Tooltip>
		);
	}

	return inner;
}

// ---------------------------------------------------------------------------
// ModuleItem — expandable module with sub-navigation and pin support
// ---------------------------------------------------------------------------

function ModuleItem({
	module: ws,
	isActive,
	collapsed,
	wsFeatures,
	t,
	favorites,
	onPin,
	onUnpin,
}: {
	module: ModuleDef;
	isActive: boolean;
	collapsed: boolean;
	wsFeatures: string[];
	t: (k: string) => string;
	favorites: string[];
	onPin: (path: string) => void;
	onUnpin: (path: string) => void;
}) {
	const filteredItems = ws.items.filter(
		(item) => !item.feature || wsFeatures.includes(item.feature),
	);

	const moduleLink = (
		<Link
			to={filteredItems[0]?.to ?? ws.prefix}
			className={cn(
				"flex items-center gap-2 rounded-md px-2 py-1.5 text-sm font-medium transition-colors",
				collapsed && "justify-center px-0",
				isActive
					? "bg-primary-action text-primary-action-foreground"
					: "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
			)}
		>
			<ws.icon className={cn("shrink-0", collapsed ? "h-5 w-5" : "h-4 w-4")} />
			{!collapsed && <span className="truncate">{t(ws.labelKey)}</span>}
		</Link>
	);

	if (collapsed) {
		return (
			<Tooltip>
				<TooltipTrigger asChild>{moduleLink}</TooltipTrigger>
				<TooltipContent side="right" sideOffset={8}>
					{t(ws.labelKey)}
				</TooltipContent>
			</Tooltip>
		);
	}

	// Modules with their own menu bar don't need sidebar sub-items
	if (ws.hasMenuBar) {
		return <div>{moduleLink}</div>;
	}

	// Collect groups
	const groups: { key: string | undefined; items: ModuleNavItem[] }[] = [];
	let currentGroup: string | undefined = "__INIT__";
	for (const item of filteredItems) {
		if (item.group !== currentGroup) {
			currentGroup = item.group;
			groups.push({ key: currentGroup, items: [] });
		}
		groups[groups.length - 1].items.push(item);
	}

	return (
		<div>
			{moduleLink}
			{isActive && (
				<div className="ml-3 mt-0.5 space-y-1 border-l pl-2">
					{groups.map((group) => (
						<div key={group.key ?? "default"}>
							{group.key && groups.length > 1 && (
								<p className="px-2 pt-1.5 pb-0.5 text-[10px] font-medium text-muted-foreground/50 uppercase tracking-wider">
									{t(group.key)}
								</p>
							)}
							<div className="space-y-0.5">
								{group.items.map((item) => {
									const isFav = favorites.includes(item.to);
									return (
										<Link
											key={item.to}
											to={item.to}
											activeOptions={{ exact: item.exact }}
											className="flex items-center gap-2 rounded-md px-2 py-1 text-[13px] font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground [&.active]:text-foreground group"
										>
											<item.icon className="h-3.5 w-3.5" />
											<span className="flex-1 truncate">
												{t(item.labelKey)}
											</span>
											<Button
												type="button"
												variant="ghost"
												size="icon"
												className={cn(
													"h-5 w-5",
													isFav
														? "text-amber-500 opacity-100"
														: "opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-foreground",
												)}
												onClick={(e) => {
													e.preventDefault();
													e.stopPropagation();
													isFav ? onUnpin(item.to) : onPin(item.to);
												}}
												title={t(
													isFav ? "remove_from_favorites" : "add_to_favorites",
												)}
												aria-label={t(
													isFav ? "remove_from_favorites" : "add_to_favorites",
												)}
											>
												<Star
													className={cn("h-3 w-3", isFav && "fill-current")}
												/>
											</Button>
										</Link>
									);
								})}
							</div>
						</div>
					))}
				</div>
			)}
		</div>
	);
}
